/** Platform-owned business permissions; organisation and actor come from the authenticated host. */
export const BUSINESS_PERMISSION_CAPABILITIES = ['accounts.read', 'cards.read', 'cards.freeze'] as const;
export type BusinessPermissionCapability = (typeof BUSINESS_PERMISSION_CAPABILITIES)[number];
export type BusinessPermissionResourceType = 'account' | 'card';
export interface BusinessPermissionStatus {
  scoped: boolean;
  owner: boolean;
}
export interface BusinessDepartment {
  id: string;
  name: string;
}
export interface BusinessDepartmentMember {
  userId: string;
  isManager: boolean;
  capabilities: BusinessPermissionCapability[];
}
export interface BusinessDepartmentResource {
  resourceType: BusinessPermissionResourceType;
  resourceId: string;
}
export interface BusinessResourceGrant extends BusinessDepartmentResource {
  userId: string;
  capabilities: BusinessPermissionCapability[];
}
export interface BusinessPermissionMutationResult {
  removed: boolean;
}

export interface BusinessPermissionsApiOperations {
  'businessPermissions.status': { payload: Record<string, never>; result: BusinessPermissionStatus };
  'businessPermissions.enable': { payload: Record<string, never>; result: BusinessPermissionStatus };
  'businessPermissions.departments.list': { payload: Record<string, never>; result: BusinessDepartment[] };
  'businessPermissions.departments.create': { payload: { name: string }; result: BusinessDepartment };
  'businessPermissions.members.list': {
    payload: { departmentId: string };
    result: BusinessDepartmentMember[];
  };
  'businessPermissions.members.set': {
    payload: {
      departmentId: string;
      userId: string;
      capabilities: BusinessPermissionCapability[];
      isManager?: boolean;
    };
    result: BusinessDepartmentMember;
  };
  'businessPermissions.members.remove': {
    payload: { departmentId: string; userId: string };
    result: BusinessPermissionMutationResult;
  };
  'businessPermissions.resources.list': {
    payload: { departmentId: string };
    result: BusinessDepartmentResource[];
  };
  'businessPermissions.resources.assign': {
    payload: { departmentId: string; resourceType: BusinessPermissionResourceType; resourceId: string };
    result: BusinessDepartmentResource;
  };
  'businessPermissions.resources.remove': {
    payload: { departmentId: string; resourceType: BusinessPermissionResourceType; resourceId: string };
    result: BusinessPermissionMutationResult;
  };
  'businessPermissions.grants.list': { payload: { userId: string }; result: BusinessResourceGrant[] };
  'businessPermissions.grants.set': {
    payload: {
      userId: string;
      resourceType: BusinessPermissionResourceType;
      resourceId: string;
      capabilities: BusinessPermissionCapability[];
    };
    result: BusinessResourceGrant;
  };
  'businessPermissions.grants.remove': {
    payload: { userId: string; resourceType: BusinessPermissionResourceType; resourceId: string };
    result: BusinessPermissionMutationResult;
  };
}
